import { useState, useEffect } from "react"
import { User } from "../types/UserClass"
import { PaddlePrice } from "../types/PaddlePrice"

export const emailRegex = /^.+@.+\..+$/
export const alphanumericRegex = /^[\w\-\s]+$/

export const useEmailRegex = (email: string) => {
    return emailRegex.test(email)
}

export const useDebounce = <T extends number | string | object>(
    value: T,
    delay: number
) => {
    // State and setters for debounced value
    const [debouncedValue, setDebouncedValue] = useState(value)
    useEffect(
        () => {
            // Update debounced value after delay
            const handler = setTimeout(() => {
                setDebouncedValue(value)
            }, delay)
            // Cancel the timeout if value changes (also on delay change or unmount)
            // This is how we prevent debounced value from updating if value is changed ...
            // .. within the delay period. Timeout gets cleared and restarted.
            return () => {
                clearTimeout(handler)
            }
        },
        [value, delay] // Only re-call effect if value or delay changes
    )
    return debouncedValue
}

export const useDebounceQueue = (delay: number) => {
    const [debouncedValue, setDebouncedValue] = useState<
        Record<string, () => void>
    >({})
    const addToQueue = (key: string, value: () => void) => {
        setDebouncedValue({ ...debouncedValue, [key]: value })
    }
    useEffect(() => {
        const handler = setTimeout(() => {
            Object.values(debouncedValue).forEach((func) => func())
            setDebouncedValue({})
        }, delay)

        return () => {
            clearTimeout(handler)
        }
    }, [debouncedValue, delay])
    return addToQueue
}

export const useDateFormatter = (user: User, date?: Date | string) => {
    if (date === undefined)
        return {
            dayMonth: "--",
            weekday: "-----",
            monthLong: "-----",
            monthShort: "---",
            time: "--:--",
        }
    if (typeof date === "string") date = new Date(date)
    const formatLocale = (locale?: string): string => {
        if (!locale) return "default"
        const normalized = locale.replace(/_/g, "-")
        try {
            new Intl.Locale(normalized)
            return normalized
        } catch {
            return "default"
        }
    }
    const locale = formatLocale(user.locale ?? undefined)

    return {
        dayMonth: date.toLocaleString(locale, {
            day: "2-digit",
            timeZone: user?.timezone ?? undefined,
        }),
        weekday: date.toLocaleString(locale, {
            weekday: "long",
            timeZone: user?.timezone ?? undefined,
        }),
        monthLong: date.toLocaleString(locale, {
            month: "long",
            timeZone: user?.timezone ?? undefined,
        }),
        monthShort: date.toLocaleString(locale, {
            month: "short",
            timeZone: user?.timezone ?? undefined,
        }),
        yearShort: date.toLocaleString(locale, {
            year: "2-digit",
            timeZone: user?.timezone ?? undefined,
        }),
        yearLong: date.toLocaleString(locale, {
            year: "numeric",
            timeZone: user?.timezone ?? undefined,
        }),
        time: date.toLocaleString(locale, {
            hour12: false,
            timeStyle: "short",
            timeZone: user?.timezone ?? undefined,
        }),
    }
}
export function useFormatVerboseDate(value?: string | null) {
    if (!value) return undefined
    const d = new Date(value)
    const day = d.getDate()
    const suffix =
        day % 10 === 1 && day !== 11
            ? "st"
            : day % 10 === 2 && day !== 12
              ? "nd"
              : day % 10 === 3 && day !== 13
                ? "rd"
                : "th"
    return `${day}${suffix} ${d.toLocaleString("en-GB", { month: "long" })} ${d.getFullYear()}`
}

export function useTimeAgoFormatter(date: Date | null) {
    if (!date) return null
    const seconds = Math.floor((Date.now() - date.getTime()) / 1000)

    const intervals = [
        { label: "year", seconds: 31536000 },
        { label: "month", seconds: 2592000 },
        { label: "week", seconds: 604800 },
        { label: "day", seconds: 86400 },
        { label: "hour", seconds: 3600 },
        { label: "minute", seconds: 60 },
        { label: "second", seconds: 1 },
    ]

    for (const interval of intervals) {
        const count = Math.floor(seconds / interval.seconds)
        if (count >= 1) {
            return `${count} ${interval.label}${count !== 1 ? "s" : ""} ago`
        }
    }

    return "just now"
}

export const useInviteDateFormatter = (
    startDate?: Date | string,
    endDate?: Date | string
) => {
    if (startDate === undefined || endDate === undefined) return ""
    if (typeof startDate === "string") startDate = new Date(startDate)
    if (typeof endDate === "string") endDate = new Date(endDate)
    return `${startDate.toLocaleString("default", { day: "2-digit" })} ${startDate.toLocaleString("default", { month: "short" })} ${startDate.toLocaleTimeString(
        "default",
        {
            hour12: true,
            timeStyle: "short",
        }
    )} - ${endDate.toLocaleTimeString("default", {
        hour12: true,
        timeStyle: "short",
    })}`
}

export const useWindowSize = () => {
    const [size, setSize] = useState({
        width: window.innerWidth,
        height: window.innerHeight,
    })

    useEffect(() => {
        const listenerFn = () =>
            setSize({ width: window.innerWidth, height: window.innerHeight })

        window.addEventListener("resize", listenerFn)

        return () => {
            window.removeEventListener("resize", listenerFn)
        }
    })

    return size
}

export const getCurrencySymbol = (currencyCode: string) => {
    switch (currencyCode) {
        case "USD":
            return "$"
        case "EUR":
            return "€"
        case "GBP":
            return "£"
        default:
            return undefined
    }
}

export const formatPaddlePrice = (priceObj?: PaddlePrice) => {
    if (!priceObj) return null
    const priceAmount = priceObj
        ? Number(priceObj.unit_price.amount) / 100
        : null
    return priceObj.billing_cycle?.interval === "year"
        ? `${getCurrencySymbol(priceObj.unit_price.currency_code)}${(Math.round((Number(priceAmount) / 12) * 2) / 2 - 0.01).toFixed(2)}`
        : `${getCurrencySymbol(priceObj.unit_price.currency_code)}${priceAmount}`
}
